// FlightSearchForm — Presentational, fully-controlled search form for the
// FlightLookup panel: the Airline Code / Flight Number / Date row, the optional
// From Airport / To Airport row, the inline error banner, and the
// "Look Up Flight" / "Looking Up Flight..." submit button with its
// loading/disabled state.
//
// Extracted verbatim from `FlightLookup.js` (the `FlightFormFields` block, the
// `ErrorBanner` placement, and the lookup button) as part of the
// behavior-preserving refactor (issue #240). The source rendered these three
// pieces in this exact order inside the panel body, so they are composed here in
// the same order to keep the characterization snapshots matching once the
// container is rewired (task 9.7). Render-only, wrapped in React.memo, with NO
// internal form state — every value, change handler, and disabled flag is passed
// in via props. Placeholders ("AA, UA, DL", "1234", "DFW, LAX, JFK",
// "LAX, DFW, ORD"), labels, the date input type, icons (lucide-react), styles,
// and attributes are identical to the source.
//
// Props:
//   lookupForm     - { airline, flightNumber, date, departureAirport, arrivalAirport }
//   loading        - boolean; drives the disabled state and button label/color
//   error          - error message string passed through to the inline ErrorBanner
//   onLookupChange - (field, value) => void for airline/flightNumber/airport inputs
//   onDateChange   - (value) => void for the date input
//   onLookupFlight - () => void invoked when the "Look Up Flight" button is clicked
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Search } from 'lucide-react';
import ErrorBanner from './ErrorBanner';

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '16px',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px'
};

export const FlightSearchForm = React.memo(function FlightSearchForm({
  lookupForm,
  loading,
  error,
  onLookupChange,
  onDateChange,
  onLookupFlight
}) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div>
          <label style={labelStyle}>
            Airline Code *
          </label>
          <input
            type="text"
            value={lookupForm.airline}
            onChange={(e) => onLookupChange('airline', e.target.value)}
            placeholder="AA, UA, DL"
            required
            maxLength={3}
            disabled={loading}
            style={{ ...inputStyle, textTransform: 'uppercase' }}
          />
        </div>

        <div>
          <label style={labelStyle}>
            Flight Number *
          </label>
          <input
            type="text"
            value={lookupForm.flightNumber}
            onChange={(e) => onLookupChange('flightNumber', e.target.value)}
            placeholder="1234"
            required
            disabled={loading}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>
            Date *
          </label>
          <input
            type="date"
            value={lookupForm.date}
            onChange={(e) => onDateChange(e.target.value)}
            required
            disabled={loading}
            style={inputStyle}
          />
        </div>
      </div>

      {/* Airport Fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div>
          <label style={labelStyle}>
            From Airport (Optional)
          </label>
          <input
            type="text"
            value={lookupForm.departureAirport || ''}
            onChange={(e) => onLookupChange('departureAirport', e.target.value)}
            placeholder="DFW, LAX, JFK"
            maxLength={4}
            disabled={loading}
            style={{ ...inputStyle, textTransform: 'uppercase' }}
          />
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Helps find the specific flight route
          </div>
        </div>

        <div>
          <label style={labelStyle}>
            To Airport (Optional)
          </label>
          <input
            type="text"
            value={lookupForm.arrivalAirport || ''}
            onChange={(e) => onLookupChange('arrivalAirport', e.target.value)}
            placeholder="LAX, DFW, ORD"
            maxLength={4}
            disabled={loading}
            style={{ ...inputStyle, textTransform: 'uppercase' }}
          />
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Helps find the specific flight route
          </div>
        </div>
      </div>

      <ErrorBanner error={error} />

      <button
        type="button"
        onClick={onLookupFlight}
        disabled={loading}
        style={{
          width: '100%',
          padding: '12px 20px',
          backgroundColor: loading ? '#9ca3af' : '#0ea5e9',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Search size={20} />
        {loading ? 'Looking Up Flight...' : 'Look Up Flight'}
      </button>
    </>
  );
});

export default FlightSearchForm;
